import React, { createContext, useContext, useState, useEffect } from 'react';
import { Subscription } from '../types/auth';

interface PaymentMethod {
  id: string;
  type: 'alipay' | 'wechat' | 'card';
  last4?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  paymentMethods: PaymentMethod[];
  isLoading: boolean;
  subscribe: (planId: string, paymentMethodId: string) => Promise<void>;
  unsubscribe: () => Promise<void>;
  addPaymentMethod: (paymentMethod: Omit<PaymentMethod, 'id'>) => Promise<void>;
  removePaymentMethod: (paymentMethodId: string) => Promise<void>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  getInvoices: () => Promise<any[]>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 模拟加载订阅信息
    const loadSubscriptionData = async () => {
      try {
        setIsLoading(true);

        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000));

        // 设置模拟数据
        setSubscription({
          id: 'sub_123',
          plan: 'free',
          status: 'active',
          currentPeriodStart: new Date('2024-09-16'),
          currentPeriodEnd: new Date('2026-12-30'),
          cancelAtPeriodEnd: false,
          features: {
            maxScriptsPerMonth: 3,
            maxCharacters: 5,
            hasAdvancedStoryboard: false,
            hasAICharacterGeneration: false,
            hasMultipleExportFormats: false,
            hasPrioritySupport: false
          }
        });

        setPaymentMethods([
          {
            id: 'pm_1',
            type: 'alipay',
            isDefault: true
          }
        ]);
      } catch (error) {
        console.error('Failed to load subscription data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptionData();
  }, []);

  const subscribe = async (planId: string, paymentMethodId: string) => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 2000));

      // 更新订阅信息
      const planFeatures = {
        free: {
          maxScriptsPerMonth: 3,
          maxCharacters: 5,
          maxProjects: 3,
          hasAdvancedStoryboard: false,
          hasAICharacterGeneration: false,
          hasMultipleExportFormats: false,
          hasPrioritySupport: false
        },
        pro: {
          maxScriptsPerMonth: Infinity,
          maxCharacters: Infinity,
          maxProjects: Infinity,
          hasAdvancedStoryboard: true,
          hasAICharacterGeneration: true,
          hasMultipleExportFormats: true,
          hasPrioritySupport: true
        },
        enterprise: {
          maxScriptsPerMonth: Infinity,
          maxCharacters: Infinity,
          maxProjects: Infinity,
          hasAdvancedStoryboard: true,
          hasAICharacterGeneration: true,
          hasMultipleExportFormats: true,
          hasPrioritySupport: true
        }
      };

      setSubscription({
        id: 'sub_' + Date.now(),
        plan: planId,
        status: 'active',
        currentPeriodStart: new Date(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30天后
        cancelAtPeriodEnd: false,
        features: planFeatures[planId as keyof typeof planFeatures]
      });

    } catch (error) {
      console.error('Failed to subscribe:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (subscription) {
        setSubscription({
          ...subscription,
          cancelAtPeriodEnd: true
        });
      }

    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addPaymentMethod = async (paymentMethod: Omit<PaymentMethod, 'id'>) => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newPaymentMethod: PaymentMethod = {
        ...paymentMethod,
        id: 'pm_' + Date.now()
      };

      setPaymentMethods(prev => [...prev, newPaymentMethod]);

    } catch (error) {
      console.error('Failed to add payment method:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removePaymentMethod = async (paymentMethodId: string) => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 500));

      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));

    } catch (error) {
      console.error('Failed to remove payment method:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const setDefaultPaymentMethod = async (paymentMethodId: string) => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 500));

      setPaymentMethods(prev =>
        prev.map(pm => ({
          ...pm,
          isDefault: pm.id === paymentMethodId
        }))
      );

    } catch (error) {
      console.error('Failed to set default payment method:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getInvoices = async () => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 返回模拟发票数据
      return [
        {
          id: 'inv_1',
          amount: 29,
          currency: 'CNY',
          status: 'paid',
          date: new Date('2024-09-16'),
          description: '专业版月费'
        },
        {
          id: 'inv_2',
          amount: 29,
          currency: 'CNY',
          status: 'paid',
          date: new Date('2024-08-16'),
          description: '专业版月费'
        }
      ];

    } catch (error) {
      console.error('Failed to get invoices:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: SubscriptionContextType = {
    subscription,
    paymentMethods,
    isLoading,
    subscribe,
    unsubscribe,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    getInvoices
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
